import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircleIcon, InfoIcon, RotateCcwIcon } from 'lucide-react';
import { PRIVACY_URL } from '../data/site';
import { loadRecaptcha, RECAPTCHA_SITE_KEY } from '../utils/recaptcha';
const SERVICE_OPTIONS = ['Auto Glass', 'Residential Glass', 'Glass Shower Doors', 'Commercial Glass'];
interface Values {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}
const EMPTY: Values = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: ''
};
type Errors = Partial<Record<keyof Values | 'captcha', string>>;
type CaptchaStatus = 'loading' | 'ready' | 'error';
interface QuoteFormProps {
  idPrefix: string;
  title?: string;
  description?: string;
  className?: string;
  headingLevel?: 2 | 3;
}
const fieldClass = 'box-border block w-full rounded border border-kosin-line bg-white px-3 py-[7px] text-[16px] leading-6 text-kosin-ink placeholder:text-kosin-body/60';
const labelClass = 'block text-[13px] font-semibold text-kosin-charcoal';
export function QuoteForm({
  idPrefix,
  title = 'Request A Free Quote',
  description = '',
  className = '',
  headingLevel = 2
}: QuoteFormProps) {
  const Heading = (headingLevel === 3 ? 'h3' : 'h2') as 'h2' | 'h3';
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [captchaStatus, setCaptchaStatus] = useState<CaptchaStatus>('loading');
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const id = (name: string) => `${idPrefix}-${name}`;
  useEffect(() => {
    let cancelled = false;
    setCaptchaStatus('loading');
    loadRecaptcha().then(() => {
      if (cancelled || !captchaRef.current || !window.grecaptcha) return;
      if (widgetIdRef.current === null) {
        captchaRef.current.innerHTML = '';
        widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          size: captchaRef.current.clientWidth < 304 ? 'compact' : 'normal',
          callback: () => {
            setCaptchaChecked(true);
            setErrors(prev => ({
              ...prev,
              captcha: undefined
            }));
          },
          'expired-callback': () => setCaptchaChecked(false),
          'error-callback': () => {
            setCaptchaChecked(false);
            setCaptchaStatus('error');
          }
        });
      }
      setCaptchaStatus('ready');
    }).catch(() => {
      if (!cancelled) setCaptchaStatus('error');
    });
    return () => {
      cancelled = true;
    };
  }, [retryKey]);
  useEffect(() => {
    return () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
      }
    };
  }, []);
  const update = useCallback((field: keyof Values, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
    setSubmitted(false);
  }, []);
  const handleRetry = () => {
    widgetIdRef.current = null;
    setCaptchaChecked(false);
    setRetryKey(k => k + 1);
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next: Errors = {};
    if (!values.name.trim()) next.name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    if (values.phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a 10-digit phone number.';
    if (!values.service) next.service = 'Please choose a service.';
    if (captchaStatus !== 'ready') next.captcha = 'Verification is unavailable. Please try again.';else if (!captchaChecked) next.captcha = 'Please complete the reCAPTCHA.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setSubmitted(false);
      const firstField = Object.keys(next).find(key => key !== 'captcha');
      if (firstField) document.getElementById(id(firstField))?.focus();
      return;
    }

    /* Design preview only: nothing is transmitted or stored. */
    setSubmitted(true);
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
      setCaptchaChecked(false);
    }
  };
  const errorText = (key: keyof Errors) => errors[key] ? <p id={id(`${key}-error`)} role="alert" className="mt-1 flex items-start gap-1 text-[12px] text-kosin-red" data-id="element-203" data-mp="3ciw8yyalfj7y">
        <AlertCircleIcon className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-204" data-mp="37jjje5sc7y1o" />
        {errors[key]}
      </p> : null;
  return <div className={`box-border w-full rounded bg-white p-4 shadow-[0_10px_30px_rgba(22,22,22,0.14)] sm:p-5 ${className}`} data-id="element-205" data-mp="33ipyzme3t1dj">
      <Heading className="text-[19px] font-bold leading-tight text-kosin-charcoal" data-id="element-206" data-mp="1j2i70h5hzai5">
        {title}
      </Heading>
      {description && <p className="mt-1 text-[13px] leading-snug text-kosin-body" data-id="element-207" data-mp="1s0kbj6ofwla6">
        {description}
      </p>}

      <form id={id('form')} className="box-border mt-3 w-full" noValidate onSubmit={handleSubmit} data-id="element-208" data-mp="1x06qplntwblm">
        <div className="space-y-2" data-id="element-209" data-mp="zcxy76peu2a4">
          <div data-id="element-210" data-mp="zdzlqanm4h8q">
            <label className={labelClass} htmlFor={id('name')} data-id="element-211" data-mp="zabpv4up3iqu">
              Full Name
            </label>
            <input id={id('name')} name="name" type="text" autoComplete="name" required value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? id('name-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-212" data-mp="19acdh2zlkl0u" />
            {errorText('name')}
          </div>

          <div className="grid grid-cols-1 gap-2.5 min-[420px]:grid-cols-2" data-id="element-213" data-mp="dhpjf7ve3b6h">
            <div data-id="element-214" data-mp="9i8cea0be5z7">
              <label className={labelClass} htmlFor={id('email')} data-id="element-215" data-mp="9gntxir9ydhp">
                Email Address
              </label>
              <input id={id('email')} name="email" type="email" autoComplete="email" required value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? id('email-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-216" data-mp="ib26veoc1bxn" />
              {errorText('email')}
            </div>
            <div data-id="element-217" data-mp="1fxrcrzm5o8os">
              <label className={labelClass} htmlFor={id('phone')} data-id="element-218" data-mp="1fx9dsv83zgd6">
                Phone Number
              </label>
              <input id={id('phone')} name="phone" type="tel" autoComplete="tel" required value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? id('phone-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-219" data-mp="1az7uc96rtf16" />
              {errorText('phone')}
            </div>
          </div>

          <div data-id="element-220" data-mp="3gmf8p3gx7cch">
            <label className={labelClass} htmlFor={id('service')} data-id="element-221" data-mp="3gmx3rwrujkit">
              What Are You Interested In?
            </label>
            <select id={id('service')} name="service" required value={values.service} onChange={e => update('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? id('service-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-222" data-mp="32uqwz1al2pw1">
              <option value="" data-id="element-223" data-mp="209pt3l9bsfi1">Select a service</option>
              {SERVICE_OPTIONS.map(option => <option key={option} value={option} data-id="element-224" data-mp="1vgddu1qjdl1y">
                  {option}
                </option>)}
            </select>
            {errorText('service')}
          </div>

          <div data-id="element-225" data-mp="1llbtdj77h75p">
            <label className={labelClass} htmlFor={id('message')} data-id="element-226" data-mp="1lhnxideag8nt">
              Message <span className="font-normal text-kosin-body" data-id="element-227" data-mp="2j6r1lg238xqq">(optional)</span>
            </label>
            <textarea id={id('message')} name="message" rows={2} value={values.message} onChange={e => update('message', e.target.value)} className={`mt-1 h-[58px] resize-y !leading-5 ${fieldClass}`} data-id="element-228" data-mp="2e8pfsnda2kbk" />
          </div>
        </div>

        <div className="mt-3" data-id="element-229" data-mp="xrl9q9hepov0">
          <div className="min-h-[78px] w-full max-w-[304px] overflow-visible" aria-live="polite" data-id="element-230" data-mp="trt86gqfkiz5">
            <div ref={captchaRef} data-id="element-231" data-mp="35h95nwtct7zs" />
            {captchaStatus === 'loading' && <p className="text-[13px] text-kosin-body" data-id="element-232" data-mp="39ib6uj6tzu4e">
                Loading verification…
              </p>}
            {captchaStatus === 'error' && <div className="rounded border border-kosin-line bg-kosin-mist p-3" data-id="element-233" data-mp="3j7mdlz68wmp4">
                <p className="text-[13px] text-kosin-ink" data-id="element-234" data-mp="3o660q2wks4j2">
                  Verification could not load, so the form is disabled.
                </p>
                <button type="button" onClick={handleRetry} className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-kosin-red hover:text-kosin-bright transition-colors duration-150 ease-kosin" data-id="element-235" data-mp="3t3xvd4olu4qy">
                  <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-236" data-mp="22tw5mhc09ea7" />
                  Try again
                </button>
              </div>}
          </div>
          {errorText('captcha')}
        </div>

        <button type="submit" className="mt-3 w-full rounded bg-kosin-red px-5 py-2.5 text-[16px] font-semibold text-white hover:bg-kosin-bright transition-colors duration-150 ease-kosin" data-id="element-237" data-mp="3ovml4a6c2l9f">
          Request My Free Quote
        </button>

        <div className="mt-3 flex items-start gap-1.5 text-[12px] leading-snug text-kosin-body" data-id="element-238" data-mp="7mpv1y4h9h30">
          <InfoIcon className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-239" data-mp="bm8hiyby607y" />
          <p data-id="element-240" data-mp="glurmywhb0cj">
            Preview only; no request is sent.{' '}
            <a href={PRIVACY_URL} className="font-semibold text-kosin-charcoal underline underline-offset-2 hover:text-kosin-red transition-colors duration-150 ease-kosin" data-id="element-241" data-mp="gk073lp4da8z">
              Privacy Policy
            </a>
            .
          </p>
        </div>

        <p className={submitted ? 'mt-2 text-[13px] font-semibold text-kosin-charcoal' : 'sr-only'} role="status" data-id="element-242" data-mp="v9fxh0kqndo5">
          {submitted ? 'Preview only. No request was sent.' : ''}
        </p>
      </form>
    </div>;
}